import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  TaskSchedule,
  TaskScheduleCreate,
  TaskScheduleUpdate,
  TaskScheduleFrequency,
} from '@/app/types/taskSchedule';
import { createTaskSchedule, updateTaskSchedule } from '@/app/services/taskScheduleService';
import { listUsers } from '@/app/services/userService';
import { useOrganization } from '@/app/hooks/useOrganization';

const FREQUENCIES: { value: TaskScheduleFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const TASK_TYPES = [
  { value: 'appointment', label: 'Appointment' },
  { value: 'service_reminder', label: 'Service Reminder' },
  { value: 'follow_up', label: 'Follow Up Call' },
  { value: 'insurance_renewal', label: 'Insurance Renewal' },
  { value: 'warranty_check', label: 'Warranty Check' },
  { value: 'vehicle_inspection', label: 'Vehicle Inspection' },
  { value: 'maintenance', label: 'Scheduled Maintenance' },
  { value: 'payment_followup', label: 'Payment Follow-up' },
  { value: 'feedback_call', label: 'Feedback Call' },
  { value: 'other', label: 'Other' },
];

const TASK_CATEGORIES = [
  { value: 'Service', label: 'Service' },
  { value: 'Body Work', label: 'Body Work' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Warranty', label: 'Warranty' },
  { value: 'Tyres & Alignment', label: 'Tyres & Alignment' },
  { value: 'AC & Electrical', label: 'AC & Electrical' },
  { value: 'Detailing', label: 'Detailing' },
  { value: 'Customer Care', label: 'Customer Care' },
  { value: 'Other', label: 'Other' },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const taskScheduleFormSchema = z
  .object({
    title_template: z
      .string()
      .min(1, 'Title template is required')
      .max(255, 'Title must be less than 255 characters'),
    description_template: z.string().optional(),
    frequency: z.enum(['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const),
    day_of_week: z.number().min(0).max(6).optional(),
    day_of_month: z.number().min(1).max(28).optional(),
    month_of_year: z.number().min(1).max(12).optional(),
    priority: z.string().default('medium'),
    start_date: z.date({ required_error: 'Start date is required' }),
    end_date: z.date().optional(),
    assigned_to_user_id: z.number().optional(),
    task_category: z.string().optional(),
    task_type: z.string().optional(),
    is_active: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.frequency === 'weekly' && data.day_of_week === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Day of week is required for weekly schedules',
        path: ['day_of_week'],
      });
    }
    if (
      (data.frequency === 'monthly' || data.frequency === 'quarterly') &&
      data.day_of_month === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Day of month is required for monthly/quarterly schedules',
        path: ['day_of_month'],
      });
    }
    if (data.frequency === 'yearly') {
      if (data.day_of_month === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Day of month is required for yearly schedules',
          path: ['day_of_month'],
        });
      }
      if (data.month_of_year === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Month is required for yearly schedules',
          path: ['month_of_year'],
        });
      }
    }
  });

type TaskScheduleFormValues = z.infer<typeof taskScheduleFormSchema>;

interface TaskScheduleFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: TaskSchedule | null;
  partyId: number;
  onSuccess?: (schedule: TaskSchedule) => void;
}

export default function TaskScheduleForm({
  open,
  onOpenChange,
  schedule,
  partyId,
  onSuccess,
}: TaskScheduleFormProps) {
  const queryClient = useQueryClient();
  const { currentSite } = useOrganization();
  const isEditMode = Boolean(schedule);

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => listUsers({ limit: 100 }),
    enabled: open,
  });

  const form = useForm<TaskScheduleFormValues>({
    resolver: zodResolver(taskScheduleFormSchema),
    defaultValues: {
      title_template: '',
      description_template: '',
      frequency: 'monthly',
      day_of_week: undefined,
      day_of_month: undefined,
      month_of_year: undefined,
      priority: 'medium',
      start_date: undefined,
      end_date: undefined,
      assigned_to_user_id: undefined,
      task_category: '',
      task_type: '',
      is_active: true,
    },
  });

  const watchFrequency = form.watch('frequency');

  useEffect(() => {
    if (schedule) {
      form.reset({
        title_template: schedule.title_template,
        description_template: schedule.description_template || '',
        frequency: schedule.frequency,
        day_of_week: schedule.day_of_week ?? undefined,
        day_of_month: schedule.day_of_month ?? undefined,
        month_of_year: schedule.month_of_year ?? undefined,
        priority: schedule.priority || 'medium',
        start_date: schedule.start_date ? new Date(schedule.start_date) : undefined,
        end_date: schedule.end_date ? new Date(schedule.end_date) : undefined,
        assigned_to_user_id: schedule.assigned_to_user_id ?? undefined,
        task_category: schedule.task_category || '',
        task_type: schedule.task_type || '',
        is_active: schedule.is_active,
      });
    } else {
      form.reset({
        title_template: '',
        description_template: '',
        frequency: 'monthly',
        day_of_week: undefined,
        day_of_month: undefined,
        month_of_year: undefined,
        priority: 'medium',
        start_date: undefined,
        end_date: undefined,
        assigned_to_user_id: undefined,
        task_category: '',
        task_type: '',
        is_active: true,
      });
    }
  }, [schedule, form]);

  const createMutation = useMutation({
    mutationFn: (data: TaskScheduleCreate) => createTaskSchedule(data),
    onSuccess: (newSchedule) => {
      toast.success('Task schedule created successfully');
      queryClient.invalidateQueries({ queryKey: ['task-schedules'] });
      onSuccess?.(newSchedule);
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create task schedule');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaskScheduleUpdate }) =>
      updateTaskSchedule(id, data),
    onSuccess: (updatedSchedule) => {
      toast.success('Task schedule updated successfully');
      queryClient.invalidateQueries({ queryKey: ['task-schedules'] });
      onSuccess?.(updatedSchedule);
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update task schedule');
    },
  });

  const onSubmit = (values: TaskScheduleFormValues) => {
    const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');

    if (isEditMode && schedule) {
      const updateData: TaskScheduleUpdate = {
        title_template: values.title_template,
        description_template: values.description_template || undefined,
        frequency: values.frequency,
        day_of_week: values.day_of_week,
        day_of_month: values.day_of_month,
        month_of_year: values.month_of_year,
        priority: values.priority,
        start_date: values.start_date ? formatDate(values.start_date) : undefined,
        end_date: values.end_date ? formatDate(values.end_date) : undefined,
        assigned_to_user_id: values.assigned_to_user_id,
        task_category: values.task_category || undefined,
        task_type: values.task_type || undefined,
        is_active: values.is_active,
      };
      updateMutation.mutate({ id: schedule.id, data: updateData });
    } else {
      const createData: TaskScheduleCreate = {
        title_template: values.title_template,
        description_template: values.description_template || undefined,
        party_id: partyId,
        frequency: values.frequency,
        day_of_week: values.day_of_week,
        day_of_month: values.day_of_month,
        month_of_year: values.month_of_year,
        priority: values.priority,
        start_date: formatDate(values.start_date),
        end_date: values.end_date ? formatDate(values.end_date) : undefined,
        assigned_to_user_id: values.assigned_to_user_id,
        task_category: values.task_category || undefined,
        task_type: values.task_type || undefined,
        is_active: values.is_active,
        site_id: currentSite?.id,
      };
      createMutation.mutate(createData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (open && e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
      if (open && (e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        form.handleSubmit(onSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, form, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Task Schedule' : 'Create Task Schedule'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the recurring task schedule details below.'
              : 'Set up a recurring task schedule. Tasks will be automatically generated based on the frequency.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title_template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title Template</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Service Reminder - {party_name} - {month}" {...field} />
                  </FormControl>
                  <FormDescription>
                    Use {'{party_name}'}, {'{month}'}, {'{year}'}, {'{date}'} as placeholders.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description_template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description Template</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter description template (optional)"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Frequency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Conditional frequency fields */}
            {watchFrequency === 'weekly' && (
              <FormField
                control={form.control}
                name="day_of_week"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Day of Week</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select day of week" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day) => (
                          <SelectItem key={day.value} value={day.value.toString()}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(watchFrequency === 'monthly' ||
              watchFrequency === 'quarterly' ||
              watchFrequency === 'yearly') && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="day_of_month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Day of Month</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(Number(value))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select day" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                            <SelectItem key={day} value={day.toString()}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchFrequency === 'yearly' && (
                  <FormField
                    control={form.control}
                    name="month_of_year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Month</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(Number(value))}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {MONTHS.map((month) => (
                              <SelectItem key={month.value} value={month.value.toString()}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date (Optional)</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, 'PPP') : 'No end date'}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="assigned_to_user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === 'unassigned' ? undefined : Number(value))
                      }
                      value={field.value?.toString() || 'unassigned'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a user (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {usersData?.items.map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.full_name || user.username} ({user.username})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      User to assign generated tasks to
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="task_category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)} value={field.value || '__none__'}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {TASK_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="task_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Type</FormLabel>
                  <Select onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)} value={field.value || '__none__'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select task type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {TASK_TYPES.map((tt) => (
                        <SelectItem key={tt.value} value={tt.value}>
                          {tt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Type of task to generate (e.g. appointment, service reminder)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      When active, tasks will be automatically generated based on the schedule.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditMode ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
